import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Calendar,
  Loader2,
  Link2,
  Globe,
} from "lucide-react";
import { getICalConfig, updateICalConfig, triggerICalSync, getICalExportUrl } from "@/lib/api";

export default function ICalSyncPanel({ logementId, logementTitre }) {
  const [config, setConfig] = useState({
    ical_airbnb_url: "",
    ical_booking_url: "",
    ical_last_sync: null,
  });
  const [exportUrl, setExportUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!logementId) return;
    loadConfig();
  }, [logementId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await getICalConfig(logementId);
      setConfig({
        ical_airbnb_url: res.data.ical_airbnb_url || "",
        ical_booking_url: res.data.ical_booking_url || "",
        ical_last_sync: res.data.ical_last_sync,
      });
      setExportUrl(window.location.origin + res.data.export_url);
    } catch (err) {
      toast.error("Erreur chargement config iCal");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateICalConfig(logementId, {
        ical_airbnb_url: config.ical_airbnb_url || null,
        ical_booking_url: config.ical_booking_url || null,
      });
      toast.success("Configuration iCal enregistrée");
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await triggerICalSync(logementId);
      if (res.data.synced > 0) {
        toast.success(`${res.data.synced} nouvelles dates importées`);
      } else {
        toast.info("Calendriers à jour, aucune nouvelle date");
      }
      await loadConfig();
    } catch (err) {
      toast.error("Erreur de synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyExportUrl = () => {
    navigator.clipboard.writeText(exportUrl);
    setCopied(true);
    toast.success("URL copiée dans le presse-papier");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ical-sync-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#004333]/5 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-[#004333]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#004333]">Synchronisation iCal</h3>
            <p className="text-xs text-gray-400">Import & export automatique</p>
          </div>
        </div>
        {config.ical_last_sync && (
          <span className="text-xs text-gray-400">
            Dernière synchro : {new Date(config.ical_last_sync).toLocaleString("fr-FR")}
          </span>
        )}
      </div>

      {/* Import URLs */}
      <div className="space-y-4">
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            <Globe className="w-3.5 h-3.5" />
            URL iCal Airbnb
          </label>
          <input
            type="url"
            value={config.ical_airbnb_url}
            onChange={(e) => setConfig({ ...config, ical_airbnb_url: e.target.value })}
            placeholder="https://www.airbnb.com/calendar/ical/XXXXX.ics?s=XXXXX"
            className="w-full px-4 py-2.5 border border-gray-200/80 rounded-sm text-sm focus:border-[#C9922A]/60 focus:ring-2 focus:ring-[#C9922A]/8 outline-none transition-all bg-[#FAFAF8]/50"
            data-testid="ical-airbnb-url"
          />
          <p className="text-xs text-gray-400 mt-1">
            Airbnb → Annonce → Disponibilité → Exporter le calendrier
          </p>
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            <Globe className="w-3.5 h-3.5" />
            URL iCal Booking.com
          </label>
          <input
            type="url"
            value={config.ical_booking_url}
            onChange={(e) => setConfig({ ...config, ical_booking_url: e.target.value })}
            placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?t=XXXXX"
            className="w-full px-4 py-2.5 border border-gray-200/80 rounded-sm text-sm focus:border-[#C9922A]/60 focus:ring-2 focus:ring-[#C9922A]/8 outline-none transition-all bg-[#FAFAF8]/50"
            data-testid="ical-booking-url"
          />
          <p className="text-xs text-gray-400 mt-1">
            Booking → Extranet → Tarifs et disponibilités → Synchroniser les calendriers
          </p>
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="bg-[#004333] text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#003322] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="ical-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Enregistrer
      </button>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Export URL */}
      <div>
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          <Link2 className="w-3.5 h-3.5" />
          URL d'export iCal (à coller dans Airbnb / Booking)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={exportUrl}
            readOnly
            className="flex-1 px-4 py-2.5 border border-gray-200/80 rounded-sm text-sm bg-gray-50 text-gray-500"
            data-testid="ical-export-url"
          />
          <button
            onClick={handleCopyExportUrl}
            className="px-4 py-2.5 border border-gray-200/80 rounded-sm text-sm hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
            data-testid="ical-copy-export-btn"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
            {copied ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Collez cette URL dans Airbnb (Importer un calendrier) et Booking (Ajouter une connexion) pour synchroniser vos réservations Happyhouse conciergerie.
        </p>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Manual Sync */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[#004333]">Synchronisation manuelle</p>
          <p className="text-xs text-gray-400">Synchronisation auto toutes les heures</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing || (!config.ical_airbnb_url && !config.ical_booking_url)}
          className="border border-[#004333]/20 text-[#004333] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#004333] hover:text-white hover:border-[#004333] transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="ical-sync-btn"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Synchronisation..." : "Synchroniser maintenant"}
        </button>
      </div>
    </div>
  );
}
